import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, ChevronRight } from "lucide-react";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useCms } from "@/contexts/CmsContext";

const collections = [];

const quickLinks = [
  { name: "Tous les bijoux", href: "/bijoux" },
  { name: "Commander", href: "/commander" },
  { name: "Comment ça marche", href: "/comment-ca-marche" },
];

const navLinks = [
  { name: "Accueil", href: "/" },
  { name: "Comment ça marche", href: "/comment-ca-marche" },
  { name: "À propos", href: "/a-propos" },
];

export default function Header() {
  const cms = useCms();
  const collectionsList = cms.getCollectionsList();
  const collections = collectionsList.map((c) => ({
    name: c.name,
    href: `/bijoux/${c.slug}`,
    image: c.image,
  }));
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/70 backdrop-blur-xl shadow-[0_4px_20px_rgba(197,157,217,0.15)]"
          : "bg-white/50 backdrop-blur-lg"
      } border-b border-[rgba(197,157,217,0.2)]`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" data-testid="header-logo" className="flex items-center gap-2 sm:gap-3 group">
            <img
              src="/images/logo.webp"
              alt="Sublime Résine"
              className="h-10 w-10 sm:h-12 sm:w-12 md:h-14 md:w-14 object-contain shrink-0 transition-transform group-hover:scale-105"
            />
            <span className="font-script text-2xl sm:text-3xl md:text-4xl text-brand-title leading-none transition-colors group-hover:text-brand-primary">
              Sublime Résine
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            <Link
              to="/"
              className={`px-4 py-2 text-sm font-body font-medium rounded-xl transition-colors ${
                location.pathname === "/"
                  ? "text-brand-primary bg-brand-secondary/20"
                  : "text-brand-body hover:text-brand-primary hover:bg-brand-secondary/10"
              }`}
              data-testid="nav-accueil"
            >
              Accueil
            </Link>

            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger
                    data-testid="nav-bijoux-trigger"
                    className={`px-4 py-2 text-sm font-body font-medium rounded-xl transition-colors bg-transparent ${
                      location.pathname.startsWith("/bijoux")
                        ? "text-brand-primary"
                        : "text-brand-body hover:text-brand-primary"
                    }`}
                  >
                    Bijoux
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div
                      data-testid="mega-menu"
                      className="w-[600px] p-6 bg-white/95 backdrop-blur-xl border border-[rgba(197,157,217,0.2)] rounded-2xl shadow-[0_12px_40px_rgba(122,63,145,0.12)]"
                    >
                      <div className="grid grid-cols-5 gap-6">
                        {/* Collections - 3 columns */}
                        <div className="col-span-3">
                          <p className="text-xs font-body font-semibold uppercase tracking-widest text-brand-secondary mb-3">
                            Collections
                          </p>
                          <ul className="space-y-0.5">
                            {collections.map((item) => (
                              <li key={item.href}>
                                <NavigationMenuLink asChild>
                                  <Link
                                    to={item.href}
                                    data-testid={`mega-menu-${item.href.split("/").pop()}`}
                                    className="flex items-center gap-3 px-3 py-2 text-sm font-body text-brand-body rounded-xl transition-colors hover:bg-brand-secondary/10 hover:text-brand-primary"
                                  >
                                    <img
                                      src={item.image}
                                      alt={item.name}
                                      className="w-8 h-8 rounded-lg object-cover shrink-0"
                                    />
                                    <span>{item.name}</span>
                                  </Link>
                                </NavigationMenuLink>
                              </li>
                            ))}
                          </ul>
                        </div>
                        {/* Quick Links - 2 columns */}
                        <div className="col-span-2">
                          <p className="text-xs font-body font-semibold uppercase tracking-widest text-brand-secondary mb-3">
                            Liens rapides
                          </p>
                          <ul className="space-y-0.5">
                            {quickLinks.map((item) => (
                              <li key={item.href}>
                                <NavigationMenuLink asChild>
                                  <Link
                                    to={item.href}
                                    data-testid={`mega-quick-${item.href.split("/").pop()}`}
                                    className="flex items-center gap-2 px-3 py-2 text-sm font-body text-brand-body rounded-xl transition-colors hover:bg-brand-secondary/10 hover:text-brand-primary"
                                  >
                                    <ChevronRight className="w-3 h-3 text-brand-secondary" />
                                    {item.name}
                                  </Link>
                                </NavigationMenuLink>
                              </li>
                            ))}
                          </ul>
                          <div className="mt-4 p-4 bg-brand-secondary/10 rounded-2xl">
                            <p className="text-xs font-body font-semibold text-brand-title mb-1">Personnalisation</p>
                            <p className="text-xs font-body text-brand-body/70 leading-relaxed">
                              Couleurs, brillants, photos, poils — tout est possible.
                            </p>
                          </div>
                          <div className="mt-3 p-3 bg-white/60 rounded-xl border border-[rgba(197,157,217,0.15)]">
                            <p className="text-xs font-body text-brand-body/60">
                              Photo <span className="font-semibold text-brand-primary">+5 $</span> · Poils <span className="font-semibold text-brand-primary">+10 $</span>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            {navLinks.slice(1).map((item) => (
              <Link
                key={item.href}
                to={item.href}
                data-testid={`nav-${item.href.slice(1)}`}
                className={`px-4 py-2 text-sm font-body font-medium rounded-xl transition-colors ${
                  location.pathname === item.href
                    ? "text-brand-primary bg-brand-secondary/20"
                    : "text-brand-body hover:text-brand-primary hover:bg-brand-secondary/10"
                }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* CTA Desktop */}
          <Link
            to="/commander"
            data-testid="header-cta"
            className="hidden lg:inline-flex items-center px-6 py-2.5 bg-brand-primary text-white text-sm font-body font-medium rounded-full transition-all hover:bg-brand-title hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(122,63,145,0.3)]"
          >
            Commander
          </Link>

          {/* Mobile Menu */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-trigger"
                className="lg:hidden p-2 rounded-xl text-brand-body hover:bg-brand-secondary/10 transition-colors"
              >
                <Menu className="w-6 h-6" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-[300px] bg-white/95 backdrop-blur-xl border-l border-[rgba(197,157,217,0.2)] p-0"
            >
              <SheetHeader className="p-6 pb-4 border-b border-[rgba(197,157,217,0.15)]">
                <SheetTitle className="flex items-center gap-3">
                  <img
                    src="/images/logo.webp"
                    alt="Sublime Résine"
                    className="h-10 w-10 object-contain shrink-0"
                  />
                  <span className="font-script text-3xl text-brand-title leading-none">
                    Sublime Résine
                  </span>
                </SheetTitle>
              </SheetHeader>
              <nav className="p-4 space-y-1" data-testid="mobile-nav">
                <Link
                  to="/"
                  data-testid="mobile-nav-accueil"
                  className="block px-4 py-3 text-sm font-body font-medium text-brand-body rounded-xl hover:bg-brand-secondary/10 transition-colors"
                >
                  Accueil
                </Link>

                <Accordion type="single" collapsible>
                  <AccordionItem value="bijoux" className="border-none">
                    <AccordionTrigger
                      data-testid="mobile-nav-bijoux-trigger"
                      className="px-4 py-3 text-sm font-body font-medium text-brand-body rounded-xl hover:bg-brand-secondary/10 hover:no-underline"
                    >
                      Bijoux
                    </AccordionTrigger>
                    <AccordionContent className="pl-2 pb-0">
                      <Link
                        to="/bijoux"
                        data-testid="mobile-nav-tous-bijoux"
                        className="flex items-center gap-2 px-4 py-2.5 text-sm font-body text-brand-primary font-medium rounded-xl hover:bg-brand-secondary/10 transition-colors"
                      >
                        Tous les bijoux
                      </Link>
                      {collections.map((item) => (
                        <Link
                          key={item.href}
                          to={item.href}
                          data-testid={`mobile-nav-${item.href.split("/").pop()}`}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm font-body text-brand-body rounded-xl hover:bg-brand-secondary/10 transition-colors"
                        >
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-6 h-6 rounded-md object-cover shrink-0"
                          />
                          {item.name}
                        </Link>
                      ))}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>

                {navLinks.slice(1).map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    data-testid={`mobile-nav-${item.href.slice(1)}`}
                    className="block px-4 py-3 text-sm font-body font-medium text-brand-body rounded-xl hover:bg-brand-secondary/10 transition-colors"
                  >
                    {item.name}
                  </Link>
                ))}

                <div className="pt-4 px-4">
                  <Link
                    to="/commander"
                    data-testid="mobile-cta"
                    className="block w-full text-center px-6 py-3 bg-brand-primary text-white text-sm font-body font-medium rounded-full transition-colors hover:bg-brand-title"
                  >
                    Commander
                  </Link>
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
